import { useState, useEffect, useRef, useCallback } from 'react'
import { BookOpen, X } from 'lucide-react'
import CopyButton from './CopyButton'
import './WebhookHelpButton.css'

const ENDPOINTS = [
  {
    method: 'POST',
    path: '/.netlify/functions/app-builder-event',
    auth: 'Bearer token via Authorization header, or secret query parameter (?secret=YOUR_SECRET)',
    requiredFields: [
      { name: 'type', type: 'string', description: 'Event type (e.g. container.started, message.done)' },
      { name: 'containerName', type: 'string', description: 'Name of the container that emitted the event' },
    ],
    optionalFields: [
      { name: 'timestamp', type: 'string', description: 'ISO-8601 timestamp of the event' },
      { name: 'data', type: 'object', description: 'Event-specific payload' },
    ],
    curl: `curl -X POST https://YOUR_SITE/.netlify/functions/app-builder-event \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -d '{"type": "container.started", "containerName": "app-building-abc123", "timestamp": "2026-02-28T12:00:00.000Z", "data": {"pushBranch": "main"}}'`,
  },
  {
    method: 'POST',
    path: '/.netlify/functions/spawn-container',
    auth: 'Bearer token via Authorization header, or secret query parameter (?secret=YOUR_SECRET)',
    requiredFields: [],
    optionalFields: [
      { name: 'prompt', type: 'string', description: 'Build prompt (falls back to default_prompt if omitted)' },
    ],
    curl: `curl -X POST https://YOUR_SITE/.netlify/functions/spawn-container \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -d '{"prompt": "Build a todo app with authentication"}'
# Returns immediately with { name, status: "pending", prompt }
# Container spawning runs in background; poll status endpoint for updates`,
  },
  {
    method: 'POST',
    path: '/.netlify/functions/update-config',
    auth: 'Bearer token via Authorization header, or secret query parameter (?secret=YOUR_SECRET)',
    requiredFields: [],
    optionalFields: [
      { name: 'prompt', type: 'string', description: 'The default prompt used when no queued apps exist' },
      { name: 'repo', type: 'string', description: 'Git repository URL to clone for builds (overrides REPO_URL env var)' },
      { name: 'branch', type: 'string', description: 'Branch to clone from the repo (default: loop-builder)' },
      { name: 'instructions', type: 'string', description: 'Instructions appended to the prompt describing tasks to add' },
      { name: 'bugFixInstructions', type: 'string', description: 'Instructions appended to the prompt when spawning a bug fix container' },
      { name: 'minContainers', type: 'number', description: 'Minimum number of containers to keep running (auto-spawns with default prompt when below)' },
      { name: 'maxContainers', type: 'number', description: 'Maximum number of containers allowed (blocks spawning when at limit)' },
      { name: 'containersPerApp', type: 'number', description: 'Number of parallel containers spawned per app (default: 3)' },
    ],
    curl: `curl -X POST https://YOUR_SITE/.netlify/functions/update-config \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -d '{"prompt": "Build a dashboard app", "minContainers": 1, "maxContainers": 5, "containersPerApp": 3}'
# All fields are optional — include only the ones you want to update`,
  },
  {
    method: 'PATCH',
    path: '/.netlify/functions/apps/:id',
    auth: 'Bearer token via Authorization header, or secret query parameter (?secret=YOUR_SECRET)',
    requiredFields: [
      { name: 'status', type: 'string', description: 'New status for the app (only "finished" is supported)' },
    ],
    optionalFields: [],
    curl: `curl -X PATCH https://YOUR_SITE/.netlify/functions/apps/APP_ID \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -d '{"status": "finished"}'
# Replace APP_ID with the ID of the app to mark as finished`,
  },
  {
    method: 'DELETE',
    path: '/.netlify/functions/apps/:id',
    auth: 'Bearer token via Authorization header, or secret query parameter (?secret=YOUR_SECRET)',
    requiredFields: [],
    optionalFields: [],
    curl: `curl -X DELETE https://YOUR_SITE/.netlify/functions/apps/APP_ID \\
  -H "Authorization: Bearer YOUR_TOKEN"
# Replace APP_ID with the ID of the app to delete
# Deletes the app and all associated data (activity log, containers, webhook events)`,
  },
]

function resolveCurl(curl: string): string {
  return curl.replace(/YOUR_SITE/g, window.location.host)
}

function WebhookHelpButton() {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleClose])

  useEffect(() => {
    if (open && panelRef.current) {
      const closeBtn = panelRef.current.querySelector<HTMLElement>('[data-testid="webhook-help-close"]')
      closeBtn?.focus()
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="webhook-help-btn"
        data-testid="webhook-help-button"
        onClick={() => setOpen(true)}
      >
        <BookOpen size={14} aria-hidden="true" />
        Webhook Documentation
      </button>
      {open && (
        <div
          className="webhook-help-overlay"
          data-testid="webhook-help-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose()
          }}
        >
          <div className="webhook-help-panel" data-testid="webhook-help-panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="webhook-help-dialog-title">
            <div className="webhook-help-panel__header">
              <div>
                <h2 className="webhook-help-panel__title" id="webhook-help-dialog-title">Webhook Endpoints</h2>
                <p className="webhook-help-panel__subtitle">Webhook endpoints for integrating with Loop Builder</p>
              </div>
              <button
                type="button"
                className="webhook-help-panel__close"
                data-testid="webhook-help-close"
                aria-label="Close webhook documentation"
                onClick={handleClose}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="webhook-help-panel__body">
            {ENDPOINTS.map((ep) => {
              const resolvedCurl = resolveCurl(ep.curl)
              const fullPath = `${window.location.origin}${ep.path}`
              return (
                <div
                  key={ep.path}
                  className="webhook-help-endpoint"
                  data-testid="webhook-help-endpoint"
                >
                  <div className="webhook-help-endpoint__header">
                    <span className={`webhook-help-endpoint__method webhook-help-endpoint__method--${ep.method.toLowerCase()}`} data-testid="webhook-endpoint-method">
                      {ep.method}
                    </span>
                    <span className="webhook-help-endpoint__path" data-testid="webhook-endpoint-path">
                      {ep.path}
                    </span>
                    <CopyButton text={fullPath} label="Copy URL" data-testid="webhook-endpoint-copy-path" />
                  </div>
                  <div className="webhook-help-endpoint__body">
                    <div className="webhook-help-endpoint__section">
                      <div className="webhook-help-endpoint__label">Authentication</div>
                      <div className="webhook-help-endpoint__text" data-testid="webhook-endpoint-auth">
                        {ep.auth}
                      </div>
                    </div>
                    {ep.requiredFields.length > 0 && (
                      <div className="webhook-help-endpoint__section">
                        <div className="webhook-help-endpoint__label">Required Fields</div>
                        <ul className="webhook-help-endpoint__fields" data-testid="webhook-endpoint-required-fields">
                          {ep.requiredFields.map((f) => (
                            <li key={f.name}>
                              <code>{f.name}</code> ({f.type}) — {f.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {ep.optionalFields.length > 0 && (
                      <div className="webhook-help-endpoint__section">
                        <div className="webhook-help-endpoint__label">Optional Fields</div>
                        <ul className="webhook-help-endpoint__fields" data-testid="webhook-endpoint-optional-fields">
                          {ep.optionalFields.map((f) => (
                            <li key={f.name}>
                              <code>{f.name}</code> ({f.type}) — {f.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {ep.requiredFields.length === 0 && (
                      <div className="webhook-help-endpoint__section">
                        <div className="webhook-help-endpoint__label">Required Fields</div>
                        <div className="webhook-help-endpoint__text" data-testid="webhook-endpoint-required-fields">
                          None — all fields are optional
                        </div>
                      </div>
                    )}
                    <div className="webhook-help-endpoint__section">
                      <div className="webhook-help-endpoint__curl-header">
                        <div className="webhook-help-endpoint__label">Example</div>
                        <CopyButton text={resolvedCurl} data-testid="webhook-endpoint-copy-curl" />
                      </div>
                      <code className="webhook-help-endpoint__curl" data-testid="webhook-endpoint-curl">
                        {resolvedCurl}
                      </code>
                    </div>
                  </div>
                </div>
              )
            })}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default WebhookHelpButton
